import { useMemo, useState } from 'react';
import { Box, Button, Paper, Stack } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useStringsContext } from '../../../contexts/strings';
import SceneHeader from '../../common/scene/scene-header';
import ScenePage from '../../common/scene/scene-page';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { SxStyle } from '../../../types';
import { Announcement } from './types';
import EditAnnouncementModal from './compomenents/edit-announcement-modal';
import AnnouncementHideSelect from './compomenents/announcement-hide-select';
import { useAnnouncements, AnnouncementForm } from './hooks/useAnnouncements';

const styles: SxStyle = {
  container: (theme) => ({
    width: '100%',
    height: 'calc(100% - 80px)',
    marginTop: theme.spacing(4),
  }),
  dataGrid: (theme) => ({
    '& .MuiDataGrid-root .MuiDataGrid-cell:focus, .MuiDataGrid-cell:focus-within': {
      outline: 'none',
    },
    '& .MuiDataGrid-columnHeader:focus, .MuiDataGrid-columnHeader:focus-within': {
      outline: 'none',
    },
    '& .MuiDataGrid-columnHeaderTitleContainer, .MuiDataGrid-columnHeaderTitleContainerContent': {
      width: '100%',
    },
    '& .MuiDataGrid-row:nth-of-type(odd)': {
      backgroundColor: theme.palette.shades.p10,
    },
  }),
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginBottom: 4,
  },
};

export default function AnnouncementsList() {
  const { strings } = useStringsContext();
  const [editAnnouncement, setEditAnnouncement] = useState<Announcement | null>(null);
  const createAnnouncement = () => {
    setEditAnnouncement({
      abbrv: 'all',
      type: 'WARNING',
      scene: 'myPage',
    } as Announcement);
  };
  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'abbrv',
        headerName: strings.announcements.fieldNameAbbrv,
        width: 100,

        renderCell: (params: GridRenderCellParams) => (
          <>{params.row.abbrv || strings.announcements.all}</>
        ),
      },
      {
        field: 'scene',
        headerName: strings.announcements.fieldNameScene,
        width: 100,
        renderCell: (params: GridRenderCellParams) => (
          <>{params.row.scene || strings.announcements.all}</>
        ),
      },
      { field: 'type', headerName: strings.announcements.fieldNameType, width: 100 },
      { field: 'title', headerName: strings.announcements.fieldNameTitle, width: 300 },
      {
        field: 'hidden',
        headerName: strings.announcements.fieldNameShow,
        minWidth: 100,
        maxWidth: 100,
        renderCell: (params: GridRenderCellParams) => (
          <AnnouncementHideSelect
            announcement={params.row}
            onChange={(hidden: boolean) => {
              query.updateAnnouncement({
                id: params.row.id,
                data: { hidden },
              });
            }}
            isLoading={query.updateMutation.isPending}
          />
        ),
      },
      {
        field: 'edit',
        headerName: '',
        minWidth: 100,
        maxWidth: 100,
        renderCell: (params: GridRenderCellParams) => (
          <Stack direction="row" spacing={1} alignItems="center">
            <IconButton
              onClick={(event) => {
                setEditAnnouncement({
                  ...params.row,
                  abbrv: params.row.abbrv || 'all',
                  scene: params.row.scene || 'all',
                  link: params.row.link || '',
                } as Announcement);
              }}
            >
              <EditIcon color="primary" />
            </IconButton>
            <IconButton
              onClick={() => {
                query.deleteAnnouncement(params.row.id);
              }}
              loading={query.deleteMutation.isPending}
            >
              <DeleteIcon color="error" />
            </IconButton>
          </Stack>
        ),
      },
    ],
    [strings]
  );
  const query = useAnnouncements();
  const rows = query.announcements || [];
  const isLoading = query.isLoading;

  return (
    <ScenePage title={strings.appBar.announcements} breadCrumps={[strings.appBar.superAdmin]}>
      <SceneHeader title={strings.appBar.announcements}></SceneHeader>

      <Box sx={styles.actions}>
        <Button variant="contained" color="primary" onClick={createAnnouncement}>
          {strings.announcements.createAnnouncement}
        </Button>
      </Box>
      <Paper elevation={2} sx={styles.container}>
        <DataGrid
          sx={styles.dataGrid}
          rows={rows || []}
          columns={columns}
          autoPageSize
          disableRowSelectionOnClick
          disableColumnFilter
          disableColumnMenu
          loading={isLoading}
          initialState={{
            sorting: {
              sortModel: [
                {
                  field: 'timestamp',
                  sort: 'desc',
                },
              ],
            },
          }}
        />
      </Paper>
      {editAnnouncement ? (
        <EditAnnouncementModal
          announcement={editAnnouncement}
          isLoading={
            editAnnouncement.id ? query.updateMutation.isPending : query.createMutation.isPending
          }
          onSave={(data: AnnouncementForm) => {
            if (editAnnouncement.id) {
              query.updateAnnouncement({
                id: editAnnouncement.id,
                data,
              });
            } else {
              query.createAnnouncement(data);
            }
          }}
          onClose={() => {
            setEditAnnouncement(null);
          }}
          open
        />
      ) : null}
    </ScenePage>
  );
}
